import { useCallback, useState, useEffect } from "react";
import {
    CategoryFilterOption,
    isFilterSection,
    UniversalFilterSection,
} from "web/types/universal-filters";
import { useHistoryWatcher } from "web/react/hooks/use-history-watcher/use-history-watcher";

export interface PaginationProps {
    onPageChange: (item: PaginatedItem) => void;
    paginationState: PaginationState;
}

export type PaginatedItem = UniversalFilterSection | CategoryFilterOption;

export enum PaginationExpansionState {
    Collapsed,
    // The deepest child that has been expanded (eg visible page on mobile)
    ExpandedActive,
    // All the ExpandedActive child's parents
    ExpandedInactive,
}

export class PaginationState {
    private set: Set<string>;
    private history: PaginatedItem[];

    constructor(previous?: PaginationState) {
        if (previous) {
            this.set = new Set(previous.set);
            this.history = previous.history.concat();
        } else {
            this.set = new Set();
            this.history = [];
        }
    }

    public clone(): PaginationState {
        return new PaginationState(this);
    }

    public getKey(item: PaginatedItem): string {
        if (isFilterSection(item)) {
            return item.id;
        }
        return item.name + "=" + item.value;
    }

    /**
     * Check if this particular page should be expanded/visible
     * @param item The thing being paginated (section etc)
     */
    public getExpansionState(item: PaginatedItem): PaginationExpansionState {
        let key = this.getKey(item);
        let expanded = this.set.has(key);
        if (!expanded) {
            return PaginationExpansionState.Collapsed;
        }
        // There has to be an active page for us to get here, but TS doesn't
        // know that.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        if (this.getKey(this.getActivePage()!) == key) {
            return PaginationExpansionState.ExpandedActive;
        }
        return PaginationExpansionState.ExpandedInactive;
    }

    public getActivePage(): PaginatedItem | undefined {
        return this.history[0];
    }

    public hasActivePage(): boolean {
        return this.getActivePage() != undefined;
    }

    public popPage(): PaginatedItem | undefined {
        let item = this.history.shift();
        if (!item) {
            return item;
        }
        this.set.delete(this.getKey(item));
        return item;
    }

    /**
     * For desktop mode when you want to close an item and all its children
     * @param item The top level item to pop
     */
    public popUntil(item: PaginatedItem): void {
        let targetKey = this.getKey(item);
        while (true) {
            let popped = this.popPage();
            if (!popped) {
                throw new Error("Target page wasn't in the page stack!");
            } else if (this.getKey(popped) == targetKey) {
                return;
            }
        }
    }

    public pushPage(item: PaginatedItem): void {
        this.set.add(this.getKey(item));
        this.history.unshift(item);
    }

    public canPushItem(item: PaginatedItem): boolean {
        let active = this.getActivePage();
        if (!active) {
            // If there are no active pages, you can push
            return true;
        } else if (isFilterSection(item)) {
            // You cannot push a filter section onto anything
            return false;
        } else if (isFilterSection(active)) {
            // For now, let's assume it's not possible for someone to attempt to
            //  push a grandchild of a section onto a section.
            return active.all_option_names.includes(item.name);
        }
        // Find out if the item is a child of the active item
        for (let child of active.children) {
            // It should not be possible to open `category=cases` from
            //  `product_type=Accessories` while `product_type=Bags` is open
            //  (which also has a `category=cases` child) so we should be safe to
            //  just check that name & value match.
            if (child.name == item.name && child.value == item.value) {
                // Opening a direct decendent of the active item is fine
                return true;
            }
        }
        // Opening someone else's child is not OK as it could lead to
        // inconsistencies
        return false;
    }
}

export interface PageManager {
    paginationState: PaginationState;
    popPage: (item: PaginatedItem) => void;
    popActivePage: () => void;
    pushActivePage: (item: PaginatedItem) => void;
    resetPagination: () => void;
    title: string;
}

export function usePageManager(initialTitle: string): PageManager {
    let [paginationState, setPaginationState] = useState(new PaginationState());
    let [title, setTitle] = useState(initialTitle);
    const urlData = useHistoryWatcher();

    let setTitleFromItem = useCallback(
        function setTitleFromItem(item?: PaginatedItem): void {
            if (!item) {
                setTitle(initialTitle);
                return;
            } else if (isFilterSection(item)) {
                setTitle(item.title);
            } else {
                setTitle(item.label);
            }
        },
        [initialTitle]
    );

    let pushActivePage = useCallback(
        function pushActivePage(item: PaginatedItem): void {
            let newState = paginationState.clone();
            while (!newState.canPushItem(item)) {
                newState.popPage();
            }
            newState.pushPage(item);
            setPaginationState(newState);
            setTitleFromItem(item);
        },
        [paginationState, setTitleFromItem]
    );

    let popActivePage = useCallback(
        function popActivePage(): void {
            let newState = paginationState.clone();
            let last = newState.popPage();
            if (!last) {
                // Nothing to do here
                return;
            }
            setPaginationState(newState);
            let newCurrent = newState.getActivePage();
            setTitleFromItem(newCurrent);
        },
        [paginationState, setTitleFromItem]
    );

    let popPage = useCallback(
        function popPage(item): void {
            let newState = paginationState.clone();

            newState.popUntil(item);
            setPaginationState(newState);
            let newCurrent = newState.getActivePage();
            setTitleFromItem(newCurrent);
        },
        [paginationState, setTitleFromItem]
    );

    let resetPagination = useCallback((): void => {
        setPaginationState(new PaginationState());
        setTitle(initialTitle);
    }, [initialTitle]);

    // When the back button is pushed - close all filters.
    useEffect(() => {
        if (urlData && urlData.eventType.toLowerCase() == "popstate") {
            resetPagination();
        }
        // We only want to run this effect when `urlData` changes (eg a new history event happens)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlData]);

    return {
        popPage,
        paginationState,
        popActivePage,
        pushActivePage,
        resetPagination,
        title,
    };
}
